import { useState } from "react";
import { safeHtml } from "@/lib/html";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReliabilityBadge, ConfidenceBar, Spinner } from "@/components/bits";
import { RelatedForms } from "@/components/EzapForms";
import { ResearchHistory } from "@/components/ResearchHistory";
import { toast } from "sonner";
import { Search, ShieldAlert, ExternalLink, Target, MousePointerSquareDashed, ArrowRight, CheckCircle2 } from "lucide-react";

const TYPE_COLORS = {
  Official: "bg-emerald-100 text-emerald-800",
  Government: "bg-blue-100 text-blue-800",
  News: "bg-amber-100 text-amber-800",
  Education: "bg-slate-100 text-slate-700",
};

export default function Research() {
  const { wp } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("JEE Main 2027");
  const [loading, setLoading] = useState(false);
  const [research, setResearch] = useState(null);
  const [target, setTarget] = useState(null);
  const [post, setPost] = useState(null);
  const [portion, setPortion] = useState("");
  const [creating, setCreating] = useState(false);

  const runResearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setResearch(null); setTarget(null); setPost(null);
    try {
      const { data } = await client.post("/research", { query });
      setResearch(data);
      if (!wp?.connected) toast.info("Connect WordPress to see candidate post matches.");
    } catch (e) {
      toast.error(apiErr(e, "Research failed"));
    } finally {
      setLoading(false);
    }
  };

  const selectTarget = async (c) => {
    setTarget(c); setPost(null); setPortion("");
    try {
      const { data } = await client.get(`/wp/posts/${c.id}`);
      setPost(data);
    } catch (e) {
      toast.error(apiErr(e, "Could not load post"));
    }
  };

  const captureSelection = () => {
    const sel = window.getSelection?.().toString().trim();
    if (sel && sel.length > 3) setPortion(sel);
  };

  const createProposal = async (usePortion) => {
    setCreating(true);
    try {
      const body = { research_id: research.id, post_id: target.id };
      if (usePortion && portion) body.portion = portion;
      const { data } = await client.post("/proposals", body);
      localStorage.setItem("ez_active_proposal", data.id);
      toast.success("Proposal created");
      navigate("/review");
    } catch (e) {
      toast.error(apiErr(e, "Could not create proposal"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-6xl">
      <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Deep Research & Evidence</p>
      <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1 mb-6">AI Research</h1>

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="ai-research-query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runResearch()}
            placeholder="e.g. JEE Main 2027"
            className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <button
          data-testid="ai-research-search-btn"
          onClick={runResearch}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {loading ? "Researching…" : "Run Deep Research"}
        </button>
      </div>

      {loading && <div className="mb-6"><Spinner label="Gathering authoritative evidence…" /></div>}

      {research && (
        <div className="space-y-8">
          {research.summary && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Research Summary</p>
              <p className="text-slate-700 leading-relaxed">{research.summary}</p>
            </div>
          )}

          {research.conflict && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-4 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5" />
              <div>
                <p className="font-semibold text-rose-800">BLOCKED — Source Conflict</p>
                <p className="text-sm text-rose-700 mt-1">{research.conflict_reason || "Two credible sources disagree. Draft allowed, but publishing is blocked."}</p>
              </div>
            </div>
          )}

          <RelatedForms query={query} />

          <div>
            <h2 className="font-serif text-xl font-bold text-slate-900 mb-3">Authoritative Evidence</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {research.evidence.map((ev, i) => (
                <div data-testid="authoritative-evidence-card" key={ev.url || ev.source_url || `${ev.claim || ev.title}-${i}`} className="rounded-xl border border-slate-200 bg-white p-4 animate-fade-up">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${TYPE_COLORS[ev.source_type] || TYPE_COLORS.Education}`}>{ev.source_type}</span>
                    <ReliabilityBadge level={ev.reliability} />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm">{ev.fact}</p>
                  <p className="text-sm text-slate-600 mt-1">Value: <span className="font-mono text-slate-900">{ev.proposed_value}</span></p>
                  {ev.evidence_quote && <p className="text-xs text-slate-500 mt-2 italic border-l-2 border-slate-200 pl-2">"{ev.evidence_quote}"</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <ConfidenceBar value={ev.confidence} />
                    {ev.source_url && (
                      <a href={ev.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        {ev.source} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {ev.extraction_uncertain && (
                    <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800">Manual verification recommended (source may be a scanned/OCR document).</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Candidate matches */}
          <div>
            <h2 className="font-serif text-xl font-bold text-slate-900 mb-1">Potential WordPress Matches</h2>
            <p className="text-sm text-slate-500 mb-3">You must explicitly select a target post. Nothing is auto-selected.</p>
            {!wp?.connected ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500">
                Connect WordPress to see candidate posts. <a href="/wordpress" className="text-blue-600 hover:underline">Connect now →</a>
              </div>
            ) : research.candidates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500">No candidate posts found for this topic.</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Post</th>
                      <th className="px-4 py-3">ID</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Modified</th>
                      <th className="px-4 py-3">Match</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {research.candidates.map((c) => (
                      <tr data-testid="candidate-wp-post-row" key={c.id} className={target?.id === c.id ? "bg-blue-50" : ""}>
                        <td className="px-4 py-3 max-w-xs">
                          <div className="font-semibold text-slate-800 truncate">{c.title}</div>
                          <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">{c.url?.slice(0, 40)} <ExternalLink className="h-3 w-3" /></a>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">#{c.id}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{(c.categories || []).join(", ") || "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{(c.modified || "").slice(0, 10)}</td>
                        <td className="px-4 py-3"><div><ConfidenceBar value={c.match_confidence} /><span className="text-[11px] text-slate-400">{c.match_reason}</span></div></td>
                        <td className="px-4 py-3">
                          <button data-testid="select-target-post-btn" onClick={() => selectTarget(c)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-500 hover:text-blue-600">
                            <Target className="h-3.5 w-3.5" /> Select Target
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Selected target + analyze */}
          {target && (
            <div className="rounded-xl border-2 border-blue-300 bg-blue-50/40 p-5 animate-fade-up">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
                <span className="text-xs font-mono uppercase tracking-widest text-blue-700">Selected Target Post</span>
              </div>
              <h3 className="font-serif text-lg font-bold text-slate-900">{target.title} <span className="font-mono text-sm text-slate-400">#{target.id}</span></h3>

              {post ? (
                <>
                  <p className="text-xs text-slate-500 mt-3 mb-1 flex items-center gap-1"><MousePointerSquareDashed className="h-3.5 w-3.5" /> Optional: highlight a portion of the content below to check updates on just that section.</p>
                  <div
                    data-testid="portion-text-selection-box"
                    onMouseUp={captureSelection}
                    className="max-h-64 overflow-y-auto ez-scroll rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: safeHtml(post.content_rendered || post.content) }}
                  />
                  {portion && (
                    <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono uppercase text-blue-700">Selected Portion</span>
                        <button onClick={() => setPortion("")} className="text-xs text-slate-400 hover:text-slate-600">clear</button>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-3">{portion.slice(0, 300)}</p>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={() => createProposal(false)}
                      disabled={creating}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {creating ? "Analyzing…" : "Analyze Full Post & Create Proposal"} <ArrowRight className="h-4 w-4" />
                    </button>
                    {portion && (
                      <button
                        data-testid="check-portion-updates-btn"
                        onClick={() => createProposal(true)}
                        disabled={creating}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-500 disabled:opacity-60"
                      >
                        Check Updates on Selected Portion
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-3"><Spinner label="Loading post content…" /></div>
              )}
            </div>
          )}
        </div>
      )}

      <ResearchHistory />
    </div>
  );
}
